/* ==========================================================================
   FEATURED LOOK - Web Components
   ========================================================================== */

function setupButtonStateManagement(component, form, submitBtn) {
  const cartDrawer = document.querySelector('cart-drawer');
  const originalText = submitBtn.textContent;
  let contentObserver = null;

  form.addEventListener('submit', () => {
    submitBtn.textContent = 'Loading...';
    submitBtn.disabled = true;
    
    if (cartDrawer) {
      contentObserver = new MutationObserver(() => {
        if (submitBtn.textContent === 'Loading...') {
          submitBtn.textContent = 'Added!';
          contentObserver.disconnect();
          contentObserver = null;
        }
      });
      
      contentObserver.observe(cartDrawer, { 
        childList: true, 
        subtree: true 
      });
    }
  });

  const errorHandler = (e) => {
    if (submitBtn.textContent === 'Loading...') {
      const errorMessage = e.detail?.message || e.detail?.description || e.detail?.error || e.detail || 'Error';
      submitBtn.textContent = errorMessage;
      
      if (contentObserver) {
        contentObserver.disconnect();
        contentObserver = null;
      }
      
      setTimeout(() => {
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
      }, 3000);
    }
  };
  document.addEventListener('cart:error', errorHandler);

  if (cartDrawer) {
    const drawerObserver = new MutationObserver(() => {
      const btnText = submitBtn.textContent;
      
      if (cartDrawer.open && btnText === 'Added!') {
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
      }
      
      if (!cartDrawer.open && (btnText === 'Loading...' || btnText === 'Added!')) {
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
      }
    });
    
    drawerObserver.observe(cartDrawer, { 
      attributes: true, 
      attributeFilter: ['open'] 
    });
    component.observers.push(drawerObserver);
  }

  return errorHandler;
}

/**
 * Featured Look Slider Component
 * Handles image slider functionality with navigation and dots
 */
class FeaturedLookSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.slides = [];
    this.dots = [];
    this.autoplayInterval = null;
    this.autoplayDelay = 5000;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.featured-look__slide'));
    this.dotsContainer = this.querySelector('.featured-look__dots');
    this.prevBtn = this.querySelector('.featured-look__nav-btn--prev');
    this.nextBtn = this.querySelector('.featured-look__nav-btn--next');

    if (this.slides.length === 0) return;

    this.createDots();
    this.bindEvents();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  createDots() {
    if (!this.dotsContainer) return;
    
    this.dotsContainer.innerHTML = '';
    
    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.classList.add('featured-look__dot');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      
      if (index === 0) {
        dot.classList.add('featured-look__dot--active');
      }
      
      dot.addEventListener('click', () => this.goToSlide(index));
      this.dotsContainer.appendChild(dot);
      this.dots.push(dot);
    });
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    // Click on image to open zoom modal
    this.slides.forEach(slide => {
      const img = slide.querySelector('.featured-look__image');
      if (img) {
        img.addEventListener('click', (e) => {
          e.preventDefault();
          this.openZoomModal();
        });
      }
    });

    // Pause autoplay on hover
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());

    // Touch/swipe support for mobile (no zoom, just swipe)
    let touchStartX = 0;

    this.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;
      
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }
    });

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    });
  }

  handleSwipe(startX, endX) {
    const threshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    // Update slides
    this.slides[this.currentIndex].classList.remove('featured-look__slide--active');
    this.slides[index].classList.add('featured-look__slide--active');

    // Update dots
    if (this.dots[this.currentIndex]) {
      this.dots[this.currentIndex].classList.remove('featured-look__dot--active');
    }
    if (this.dots[index]) {
      this.dots[index].classList.add('featured-look__dot--active');
    }

    this.currentIndex = index;
  }

  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }

  startAutoplay() {
    if (this.slides.length <= 1) return;
    
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  openZoomModal() {
    // Get the currently active slide's image
    const activeSlide = this.slides[this.currentIndex];
    if (!activeSlide) return;
    
    const img = activeSlide.querySelector('.featured-look__image');
    if (!img) return;
    
    const zoomSrc = img.dataset.zoomSrc || img.src;
    const alt = img.alt || 'Featured Look';
    
    document.dispatchEvent(new CustomEvent('featured-look:open-zoom', {
      detail: { src: zoomSrc, alt },
      bubbles: true
    }));
  }
}

class FeaturedLookAddAll extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitBtn = this.querySelector('.featured-look__add-all-btn');
    this.observers = [];
    this.boundErrorHandler = null;
    
    if (this.form && this.submitBtn) {
      this.boundErrorHandler = setupButtonStateManagement(this, this.form, this.submitBtn);
    }
  }

  disconnectedCallback() {
    if (this.boundErrorHandler) {
      document.removeEventListener('cart:error', this.boundErrorHandler);
    }
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

class FeaturedLookItem extends HTMLElement {
  constructor() {
    super();
    this.select = null;
    this.form = null;
    this.wishlistBtn = null;
    this.variantInput = null;
    this.observers = [];
    this.boundErrorHandler = null;
  }

  connectedCallback() {
    this.select = this.querySelector('.featured-look__item-select');
    this.form = this.querySelector('.featured-look__item-form');
    this.wishlistBtn = this.querySelector('.featured-look__item-wishlist');
    this.variantInput = this.querySelector('input[name="id"]');
    this.submitBtn = this.querySelector('.featured-look__item-btn');

    this.bindEvents();
  }

  bindEvents() {
    // Variant select change
    if (this.select && this.variantInput) {
      this.select.addEventListener('change', (e) => {
        this.handleVariantChange(e.target.value);
      });
    }

    // Button state management
    if (this.form && this.submitBtn) {
      this.boundErrorHandler = setupButtonStateManagement(this, this.form, this.submitBtn);
    }

    // Wishlist toggle
    if (this.wishlistBtn) {
      this.wishlistBtn.addEventListener('click', () => {
        this.toggleWishlist();
      });
    }
  }

  disconnectedCallback() {
    if (this.boundErrorHandler) {
      document.removeEventListener('cart:error', this.boundErrorHandler);
    }
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }

  handleVariantChange(variantId) {
    if (!variantId) return;
    
    // Update hidden input with selected variant
    if (this.variantInput) {
      this.variantInput.value = variantId;
    }

    // Dispatch custom event for any listeners
    this.dispatchEvent(new CustomEvent('variant-change', {
      detail: { variantId },
      bubbles: true
    }));
  }

  toggleWishlist() {
    const productId = this.dataset.productId;
    
    this.wishlistBtn.classList.toggle('featured-look__item-wishlist--active');
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('wishlist-toggle', {
      detail: { 
        productId,
        isActive: this.wishlistBtn.classList.contains('featured-look__item-wishlist--active')
      },
      bubbles: true
    }));
  }
}

/**
 * Featured Look Zoom Modal Component
 * Handles image zoom functionality
 */
class FeaturedLookZoomModal extends HTMLElement {
  constructor() {
    super();
    this.isZoomed = false;
    this.isDragging = false;
    this.hasDragged = false;
    this.startX = 0;
    this.startY = 0;
    this.translateX = 0;
    this.translateY = 0;
    this.lastTranslateX = 0;
    this.lastTranslateY = 0;
  }

  connectedCallback() {
    this.overlay = this.querySelector('.featured-look__zoom-overlay');
    this.closeBtn = this.querySelector('.featured-look__zoom-close');
    this.imageWrapper = this.querySelector('.featured-look__zoom-image-wrapper');
    this.image = this.querySelector('.featured-look__zoom-image');

    this.bindEvents();
    this.bindGlobalEvents();
  }

  bindEvents() {
    // Close on overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Close on button click
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Image events for zoom and drag (desktop only)
    this.isMobile = window.matchMedia('(max-width: 999px)').matches;
    
    if (this.image && !this.isMobile) {
      // Mouse events
      this.image.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.startDrag(e);
      });
      
      document.addEventListener('mousemove', (e) => this.drag(e));
      
      document.addEventListener('mouseup', (e) => {
        if (this.isDragging && !this.hasDragged) {
          // It was a click, not a drag
          this.toggleZoom(e);
        }
        this.endDrag();
      });
    }
    
    // On mobile, tap image to close modal
    if (this.image && this.isMobile) {
      this.image.addEventListener('click', () => this.close());
    }

    // Keyboard close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('featured-look__zoom-modal--open')) {
        this.close();
      }
    });
  }

  startDrag(e) {
    this.isDragging = true;
    this.hasDragged = false;
    
    if (this.isZoomed) {
      this.image.style.transition = 'none';
    }
    
    if (e.type === 'touchstart') {
      this.startX = e.touches[0].clientX;
      this.startY = e.touches[0].clientY;
      this.dragStartX = e.touches[0].clientX - this.lastTranslateX;
      this.dragStartY = e.touches[0].clientY - this.lastTranslateY;
    } else {
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.dragStartX = e.clientX - this.lastTranslateX;
      this.dragStartY = e.clientY - this.lastTranslateY;
    }
  }

  drag(e) {
    if (!this.isDragging) return;
    
    let currentX, currentY;
    if (e.type === 'touchmove') {
      currentX = e.touches[0].clientX;
      currentY = e.touches[0].clientY;
    } else {
      currentX = e.clientX;
      currentY = e.clientY;
    }
    
    // Check if moved enough to count as drag (5px threshold)
    const deltaX = Math.abs(currentX - this.startX);
    const deltaY = Math.abs(currentY - this.startY);
    
    if (deltaX > 5 || deltaY > 5) {
      this.hasDragged = true;
    }
    
    // Only move if zoomed and actually dragging
    if (this.isZoomed && this.hasDragged) {
      e.preventDefault();
      this.image.style.cursor = 'grabbing';
      
      this.translateX = currentX - this.dragStartX;
      this.translateY = currentY - this.dragStartY;
      
      this.updateImageTransform();
    }
  }

  endDrag() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.image.style.cursor = this.isZoomed ? 'grab' : 'zoom-in';
    this.image.style.transition = 'transform 0.3s ease-out';
    
    if (this.hasDragged) {
      this.lastTranslateX = this.translateX;
      this.lastTranslateY = this.translateY;
    }
  }

  updateImageTransform() {
    if (this.isZoomed) {
      this.image.style.transform = `scale(1.8) translate(${this.translateX / 1.8}px, ${this.translateY / 1.8}px)`;
    } else {
      this.image.style.transform = 'scale(1) translate(0, 0)';
    }
  }

  bindGlobalEvents() {
    // Listen for open requests from slider images
    document.addEventListener('featured-look:open-zoom', (e) => {
      this.open(e.detail.src, e.detail.alt);
    });
  }

  open(src, alt) {
    if (!this.image) return;
    
    // Reset all states
    this.resetPosition();
    this.isZoomed = false;
    this.image.classList.remove('featured-look__zoom-image--zoomed');
    this.image.style.transform = 'scale(1) translate(0, 0)';
    this.image.style.cursor = 'zoom-in';
    
    // Set image source
    this.image.src = src;
    this.image.alt = alt || 'Zoomed image';
    
    // Open modal with animation
    requestAnimationFrame(() => {
      this.classList.add('featured-look__zoom-modal--open');
      document.body.style.overflow = 'hidden';
    });
  }

  close() {
    this.classList.remove('featured-look__zoom-modal--open');
    document.body.style.overflow = '';
    
    // Reset after animation completes
    setTimeout(() => {
      this.resetPosition();
      this.isZoomed = false;
      this.image.classList.remove('featured-look__zoom-image--zoomed');
      this.image.style.transform = 'scale(1) translate(0, 0)';
      this.image.style.cursor = 'zoom-in';
    }, 400);
  }

  resetPosition() {
    this.translateX = 0;
    this.translateY = 0;
    this.lastTranslateX = 0;
    this.lastTranslateY = 0;
  }

  toggleZoom(e) {
    if (this.isZoomed) {
      // Zoom out - reset position
      this.isZoomed = false;
      this.resetPosition();
      this.image.classList.remove('featured-look__zoom-image--zoomed');
      this.image.style.transform = 'scale(1) translate(0, 0)';
      this.image.style.cursor = 'zoom-in';
    } else {
      // Zoom in
      this.isZoomed = true;
      this.image.classList.add('featured-look__zoom-image--zoomed');
      this.image.style.transform = 'scale(1.8) translate(0, 0)';
      this.image.style.cursor = 'grab';
    }
  }
}

// Register custom elements
if (!customElements.get('featured-look-slider')) {
  customElements.define('featured-look-slider', FeaturedLookSlider);
}

if (!customElements.get('featured-look-add-all')) {
  customElements.define('featured-look-add-all', FeaturedLookAddAll);
}

if (!customElements.get('featured-look-item')) {
  customElements.define('featured-look-item', FeaturedLookItem);
}

if (!customElements.get('featured-look-zoom-modal')) {
  customElements.define('featured-look-zoom-modal', FeaturedLookZoomModal);
}
